/**
 * RESPONSIBILITY: one band of an event's page.
 *
 * An event page is a stack of these. The THEME is the only field always asked for, and
 * it decides every other one: pick Questions and you are asked for questions, pick
 * Gallery and you are asked for pictures, and neither time are you shown the other. That
 * is what `showWhen` is for, and it is the difference between a form with seventeen
 * fields and a form with three.
 *
 * Hiding a field never clears it. An editor who picks Body, types six paragraphs, then
 * picks Gallery to see what it looks like still has the six paragraphs when they pick
 * Body again — the condition governs what is ASKED, not what is stored.
 *
 * Every list here could have been one generic list of generic items conditioned on the
 * theme. It is not, because a contact has a photograph and a question has an answer, and
 * a row that can be either is a row where half the columns are always wrong.
 */
import type { ImageValue } from '@framework/core';
import { Row, Text, Paragraph, Img, Bool, Link, Num, Choice, List, RenderOf } from '@framework/core/decorators';
import { SECTION_THEMES, THEME } from '../themes';
import { FactRow } from './fact';
import { ScheduleRow } from './schedule';
import { ContactRow } from './contact';
import { FaqRow } from './faq';
import { PictureRow } from './picture';

@Row()
export class EventSectionRow {
  @Choice({
    label: 'What this band is', options: SECTION_THEMES, default: THEME.Body,
    description:
      'Choose this first — it decides what you are asked for below. '
      + 'Body: words. '
      + 'Facts: a row of short answers, like When and Where and Cost. '
      + 'Note: one line set apart from the rest. '
      + 'Schedule: a list of dates, each with its own words and booking link. '
      + 'Contacts: the people to ask, with photographs. '
      + 'Questions: questions and their answers. '
      + 'Gallery: pictures with captions. '
      + 'Video: a video hosted elsewhere, with a still to show first. '
      + 'Call to action: words and a button. '
      + 'Location: an address and a link to a map. '
      + 'Related events: other events carrying a tag you choose. '
      + 'Another site: a card pointing somewhere off this site. '
      + 'Banner: a wide picture with words across it.',
  })
  theme!: string;

  /** Every theme can carry one, and an empty one draws nothing. */
  @Text({ label: 'Heading', maxLength: 300 })
  heading!: string;

  /** On unless somebody says otherwise — see commons/shown for why only a no counts. */
  @Bool({
    label: 'Show this band', default: true,
    description: 'Untick to take it off the page without deleting what is in it.',
  })
  show!: boolean;

  @Paragraph({
    label: 'Text', maxLength: 20000, rows: 5, markdown: true, allow: ['verse'],
    showWhen: { field: 'theme', is: [THEME.Body, THEME.Cta, THEME.Elsewhere, THEME.Banner, THEME.Location] },
  })
  body!: string;

  @RenderOf('body')
  bodyHtml!: string;

  @Text({
    label: 'Note', maxLength: 600,
    showWhen: { field: 'theme', is: [THEME.Note] },
    description: 'One line, set apart from the text around it.',
  })
  note!: string;

  @Img({
    label: 'Picture', folder: 'events', maxSizeMb: 12, size: '1600 × 900',
    showWhen: { field: 'theme', is: [THEME.Banner, THEME.Video, THEME.Elsewhere] },
    description: 'On a Video band this is the still shown before it is played.',
  })
  image!: ImageValue;

  @List({
    label: 'Facts', of: () => FactRow, maxItems: 8,
    showWhen: { field: 'theme', is: [THEME.Facts] },
  })
  facts!: FactRow[];

  @List({
    label: 'Dates', of: () => ScheduleRow, maxItems: 40,
    showWhen: { field: 'theme', is: [THEME.Schedule] },
  })
  schedule!: ScheduleRow[];

  @List({
    label: 'People', of: () => ContactRow, maxItems: 8,
    showWhen: { field: 'theme', is: [THEME.Contacts] },
  })
  contacts!: ContactRow[];

  @List({
    label: 'Questions', of: () => FaqRow, maxItems: 20,
    showWhen: { field: 'theme', is: [THEME.Questions] },
  })
  questions!: FaqRow[];

  @List({
    label: 'Pictures', of: () => PictureRow, maxItems: 24,
    showWhen: { field: 'theme', is: [THEME.Gallery] },
  })
  pictures!: PictureRow[];

  @Link({
    label: 'Video link',
    showWhen: { field: 'theme', is: [THEME.Video] },
    description: 'The page the video lives on. This site does not host video.',
  })
  videoHref!: string;

  @Text({
    label: 'Button text', maxLength: 120,
    showWhen: { field: 'theme', is: [THEME.Cta, THEME.Elsewhere, THEME.Banner] },
    description: 'e.g. Register, Sign up, RSVP.',
  })
  ctaLabel!: string;

  @Link({
    label: 'Button link',
    showWhen: { field: 'theme', is: [THEME.Cta, THEME.Elsewhere, THEME.Banner] },
  })
  ctaHref!: string;

  @Paragraph({
    label: 'Address', maxLength: 600, rows: 3,
    showWhen: { field: 'theme', is: [THEME.Location] },
  })
  address!: string;

  @Link({
    label: 'Map link',
    showWhen: { field: 'theme', is: [THEME.Location] },
  })
  mapHref!: string;

  @Text({
    label: 'Map link text', maxLength: 120, default: 'Open in maps',
    showWhen: { field: 'theme', is: [THEME.Location] },
  })
  mapLabel!: string;

  /**
   * Which events the Related band shows. A tag and not a hand-picked list: pick the tag
   * once and every event added to it afterwards appears here without anybody editing
   * this page again.
   */
  @Choice({
    label: 'Tagged', in: 'categories',
    showWhen: { field: 'theme', is: [THEME.Related] },
    description: 'Shows other events carrying this tag.',
  })
  tag!: string;

  @Num({
    label: 'How many', min: 1, max: 12, integer: true, default: 3,
    showWhen: { field: 'theme', is: [THEME.Related] },
  })
  count!: number;
}
